import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';
import { useAlbumContext } from '@/contexts/AlbumProvider';
import { useAdminAuth } from '@/contexts/AdminAuthContext';

export interface Message {
  id: string;
  album_id: string;
  user_id: string;
  message: string;
  created_at: string;
  updated_at: string;
  // Enriched fields from user info
  author_name?: string;
  author_avatar?: string;
}

export const useMessages = () => {
  const { user } = useAuth();
  const { albumId } = useAlbumContext();
  const { isAdmin, isSuperadmin } = useAdminAuth();

  const [messages, setMessages] = useState<Message[]>([]);
  const [loading, setLoading] = useState(false);

  // Fetch messages for the current album
  const fetchMessages = useCallback(async () => {
    if (!albumId) return;

    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('messages')
        .select('*')
        .eq('album_id', albumId)
        .order('created_at', { ascending: false });

      if (error) throw error;

      // Fetch user info for all messages
      if (data && data.length > 0) {
        const userIds = [...new Set(data.map((m) => m.user_id))];
        const { data: userInfo, error: userError } = await supabase.rpc(
          'get_user_display_info',
          { user_ids: userIds }
        );

        if (!userError && userInfo) {
          const userMap = userInfo.reduce((acc: Record<string, any>, u: any) => {
            acc[u.id] = u;
            return acc;
          }, {});

          const messagesWithUsers = data.map((msg) => ({
            ...msg,
            author_name: userMap[msg.user_id]?.display_name || 'Usuario anónimo',
            author_avatar: userMap[msg.user_id]?.avatar_url,
          }));

          setMessages(messagesWithUsers);
        } else {
          setMessages(data);
        }
      } else {
        setMessages([]);
      }
    } catch (error) {
      console.error('Error fetching messages:', error);
    } finally {
      setLoading(false);
    }
  }, [albumId]);

  // Add a new message
  const addMessage = async (messageText: string) => {
    if (!user || !albumId) return;

    try {
      const { data, error } = await supabase
        .from('messages')
        .insert({
          album_id: albumId,
          user_id: user.id,
          message: messageText.trim(),
        })
        .select()
        .single();

      if (error) throw error;

      // Optimistic update
      const optimistic: Message = {
        ...data,
        author_name:
          (user.user_metadata as any)?.full_name ||
          (user.user_metadata as any)?.name ||
          user.email?.split('@')[0] ||
          'Vos',
        author_avatar:
          (user.user_metadata as any)?.avatar_url ||
          (user.user_metadata as any)?.picture ||
          undefined,
      };
      setMessages((prev) => [optimistic, ...prev]);

      return data;
    } catch (error) {
      console.error('Error adding message:', error);
      throw error;
    }
  };

  // Edit a message (only author or superadmin)
  const editMessage = async (messageId: string, messageText: string) => {
    try {
      const { error } = await supabase
        .from('messages')
        .update({ message: messageText.trim() })
        .eq('id', messageId);

      if (error) throw error;

      // Optimistic local update
      setMessages((prev) =>
        prev.map((m) =>
          m.id === messageId ? { ...m, message: messageText.trim() } : m
        )
      );
    } catch (error) {
      console.error('Error editing message:', error);
      throw error;
    }
  };

  // Delete a message (author, admin, or superadmin)
  const deleteMessage = async (messageId: string) => {
    try {
      const { error } = await supabase
        .from('messages')
        .delete()
        .eq('id', messageId);

      if (error) throw error;

      // Optimistic local removal
      setMessages((prev) => prev.filter((m) => m.id !== messageId));
    } catch (error) {
      console.error('Error deleting message:', error);
      throw error;
    }
  };

  // Check if user can edit a specific message
  const canEdit = (messageUserId: string) => {
    if (!user) return false;
    return user.id === messageUserId || isSuperadmin;
  };

  // Check if user can delete a specific message
  const canDelete = (messageUserId: string) => {
    if (!user) return false;
    return user.id === messageUserId || isAdmin || isSuperadmin;
  };

  // Subscribe to real-time updates
  useEffect(() => {
    if (!albumId) return;

    fetchMessages();

    const channel = supabase
      .channel(`messages-${albumId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'messages',
          filter: `album_id=eq.${albumId}`,
        },
        () => {
          fetchMessages();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [albumId, fetchMessages]);

  return {
    messages,
    loading,
    addMessage,
    editMessage,
    deleteMessage,
    canEdit,
    canDelete,
    refetch: fetchMessages,
  };
};
